import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";

import { Button, Flex, Modal, Typography, App, Pagination } from "antd";

import PlusIcon from "@/icons/PlusIcon";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import DefinitionsTable from "@/components/tables/DefinitionsTable";
import DefinitionModal from "@/components/modals/DefinitionModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchFrom3 from "@/components/inputs/SearchFrom3";

import { getList, getItem, deleteItem } from "@/api/definitions";

import { TITLES, MODEL_BY_PATH } from "@/constants/definitions";

const LOCAL_STORAGE_KEY = "definitionsTableParams";

const getStorageKey = (userUuid, type) =>
  `${userUuid}_${type}_${LOCAL_STORAGE_KEY}`;

const getInitialTableParams = (userUuid, type) => {
  try {
    const saved = localStorage.getItem(getStorageKey(userUuid, type));

    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Failed to parse table params from localStorage", error);
  }

  return {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
  };
};

const { Title } = Typography;

// Страница объектов
// Содержит список объектов с возможностью добавления, редактирования и удаления
const Definitions = () => {
  const { user } = useContext(UserContext);
  const { message } = App.useApp();
  const { type } = useParams();

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Инициализация пагинации из localStorage
  const [tableParams, setTableParams] = useState(() =>
    getInitialTableParams(user.user_uuid, type),
  );

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  const [search, setSearch] = useState("");

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Запрос на сервер для получения данных
  const fetchData = async (
    pagination = tableParams.pagination,
    searchField = search,
    sorter = tableParams.sorter,
  ) => {
    setIsLoading(true);

    const params = {
      type: MODEL_BY_PATH[type],
      page: pagination?.current || 1,
      perpage: pagination?.pageSize || 10,
      search: searchField,
      ...(sorter?.order && {
        sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
      }),
    };

    try {
      const { definitions, total, page, perPage } = await getList(params);

      setData(definitions);

      setTableParams((prev) => {
        const updatedTableParams = {
          ...prev,
          pagination: {
            ...prev.pagination,
            ...pagination,
            total,
            current: page,
            pageSize: perPage,
          },
          sorter,
        };
        // Сохраняем параметры пагинации в localStorage
        localStorage.setItem(
          getStorageKey(user.user_uuid, type),
          JSON.stringify(updatedTableParams),
        );

        return updatedTableParams;
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  //  initial fetch
  useEffect(() => {
    const savedTableParams = getInitialTableParams(user.user_uuid, type);

    setSearch("");
    setTableParams(savedTableParams);

    fetchData(savedTableParams.pagination, "", savedTableParams.sorter);

    // eslint-disable-next-line
  }, [type]);

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchData(pagination, search, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchData(newPagination, search, tableParams.sorter);
  };

  // Обработчик добавления нового объекта
  const handleAddNew = () => {
    setCurrentItem(null);
    setIsModalShow(true);
  };

  // Обработчик редактирования объекта
  const handleEditItem = async (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
    setIsModalLoading(true);

    try {
      const fullItem = await getItem(item.uuid);

      setCurrentItem(fullItem);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });

      setIsModalShow(false);
    } finally {
      setIsModalLoading(false);
    }
  };

  // Обработчик сохранения объекта
  const handleSaveItem = () => {
    setIsModalShow(false);
    if (currentItem) {
      fetchData();
    } else {
      setSearch("");
      fetchData({ current: 1 }, "");
    }
  };

  // Обработчик удаления объекта
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${item.name}?`,
      content:
        "Все связанные данные будут удалены. Это действие нельзя будет отменить.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем объект после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Ресурсы",
              },
              {
                title: "Структура объектов",
              },
              {
                title: TITLES[type],
              },
            ]}
          />

          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              {TITLES[type]}
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>
        </Flex>

        <Flex gap={10} align="center">
          <div style={{ maxWidth: "300px" }}>
            <SearchFrom3
              placeholder="Поиск"
              defaultValue={search}
              onSearch={(value) => {
                setSearch(value);
                fetchData(
                  {
                    ...tableParams.pagination,
                    current: 1,
                  },
                  value,
                  tableParams.sorter,
                );
              }}
            />
          </div>
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <DefinitionsTable
            data={data}
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>

      {isModalShow && (
        <DefinitionModal
          data={currentItem}
          visible={isModalShow}
          loading={isModalLoading}
          onSave={handleSaveItem}
          onCancel={() => setIsModalShow(false)}
        />
      )}
    </>
  );
};

export default Definitions;
